import { ErrorSimple, Loader, PageHeaderSg, PagingSg, PhaseBanner, PrimaryButton, SearchAsYouTypeComponent, SelectComponent, SelectOptionProps } from "@scts-ui/component-library";
import { replaceDevLinkWithConfig, useTitleService } from "@scts/utilities";
import { getContainer, getJsonBlob } from '../../utils/decisions-utils';
import { useEffect, useState } from "react";
import { STORAGE_ACCOUNT, DATA_FILES_FOLDER, FRONT_DOOR_URL, APPEALS_FILE_NAME } from '../../../config';
import { filterAndSearchAppeals } from "../../utils/combinedFiltersAndSearch";
import { creatingFilterArraysForAppeals } from "../../utils/filter";
import { renderWithPaging } from "../../utils/paging";
import { formatDatesNoTImeForExcel } from "../../components/excel-export/formatDatesForExcel";
import AppealsTable, { renderSubComponent } from "./appeals-table";
import { AppealsParentColumns } from "./appeals-columns";
import { AppDataLocalTax } from "../../content/app-data-ltc-data";
import ExcelExport from "../../components/excel-export/excelExport";
import { appealsExcelExportParams } from "../../components/excel-export/parameters/appealsExcelExportParams";
import { LTCAppeal } from "@scts/models";


const Appeals = () => {
  useTitleService('Active Appeals');
  const itemsInAPage = 8;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [networkError, setNetworkError] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [noBlobs, setNoBlobs] = useState(false);
  const [appeals, setAppeals] = useState<LTCAppeal[]>([]);
  const [currentAppeals, setCurrentAppeals] = useState<LTCAppeal[]>([]);
  const [filteredAppeals, setFilteredAppeals] = useState<LTCAppeal[]>([]);
  const [exportableAppeals, setExportableAppeals] = useState<LTCAppeal[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  //filter arrays
  const [dates, setDates] = useState<SelectOptionProps[]>([]);
  const [appealTypes, setAppealTypes] = useState<SelectOptionProps[]>([]);
  const [appealSubTypes, setAppealSubTypes] = useState<SelectOptionProps[]>([]);
  const [respondents, setRespondents] = useState<SelectOptionProps[]>([]);
  //active filter item
  const [appealTypeFilter, setAppealTypeFilter] = useState<string>('All');
  const [appealSubTypeFilter, setAppealSubTypeFilter] = useState<string>('All');
  const [respondentFilter, setRespondentFilter] = useState<string>('All');

  useEffect(() => {
    const getAppealJson = async () => {
      if (
        STORAGE_ACCOUNT && 
        DATA_FILES_FOLDER && 
        FRONT_DOOR_URL !== undefined && 
        APPEALS_FILE_NAME 
      ) {
        const cont = getContainer(
          STORAGE_ACCOUNT,
          DATA_FILES_FOLDER,
          FRONT_DOOR_URL
        );
        try {
          setIsFetchingData(true);
          const jsonBlob = await getJsonBlob(cont, APPEALS_FILE_NAME);

          let appealDataArray = JSON.parse(jsonBlob);
          if (!jsonBlob || appealDataArray.length === 0) 
            setNoBlobs(true);
          else 
            setAppeals(appealDataArray);
        } catch (error) {
            setNetworkError(true);
        }
        finally{
          setIsFetchingData(false);
        }
      }
    };
    getAppealJson();      
  }, [STORAGE_ACCOUNT, DATA_FILES_FOLDER, FRONT_DOOR_URL, APPEALS_FILE_NAME]);

  useEffect(() => {
    if(appeals.length > 0) {
      childPageClicked(1);
      setNoBlobs(false);
      setCurrentAppeals(appeals);
    }
  }, [appeals]);

  useEffect(() => {
    if (appeals.length > 0) {
      const filteredAndSearchedAppeals: LTCAppeal[] = filterAndSearchAppeals(appeals, appealTypeFilter, appealSubTypeFilter, respondentFilter, searchTerm);
      setFilteredAppeals(filteredAndSearchedAppeals);
    }
  }, [appeals, appealSubTypeFilter, appealTypeFilter, respondentFilter, searchTerm]);

  useEffect(() => {
    if (appeals.length > 0) {
      const filterArrays = creatingFilterArraysForAppeals(filteredAppeals);
      setAppealTypes(filterArrays[0]);
      setAppealSubTypes(filterArrays[1]);
      setRespondents(filterArrays[2]);
    }
  }, [appeals, filteredAppeals]);

  useEffect(() => {
    //as a result of using the search while on, for example, the last page, the search result may fit in less pages and the below makes sure that the last page will be refreshed and correctly numbered
    if ( 
      currentPage > Math.ceil(filteredAppeals.length / itemsInAPage) && 
      currentPage !== 1 && 
      filteredAppeals.length !== 0
    ) {
      setCurrentPage(Math.ceil(filteredAppeals.length / itemsInAPage));
    } else {
      setCurrentAppeals(renderWithPaging(filteredAppeals, currentPage, itemsInAPage));
    }
  }, [filteredAppeals, currentPage, itemsInAPage]);

  useEffect(() => {
    setExportableAppeals(() => 
      filteredAppeals.map((appeal) => ({
        ...appeal,
        dateReceived: formatDatesNoTImeForExcel(appeal.dateReceived),
      }))
    );
  }, [filteredAppeals]);

  const childPageClicked = (page: number) => {
    setCurrentPage(page);
  };  
  
  const onSearchBoxChange = (searchInput: string) => {
    setSearchTerm(searchInput);
  };

  const onAppealTypeFilterChange = (option: HTMLOptionElement) => {
    setAppealTypeFilter(option.value);
  };
  
  const onAppealSubTypeFilterChange = (option: HTMLOptionElement) => {
    setAppealSubTypeFilter(option.value);
  };

  const onRespondentFilterChange = (option: HTMLOptionElement) => {
    setRespondentFilter(option.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setAppealTypeFilter('All');
    setRespondentFilter('All');
    setAppealSubTypeFilter('All');
    setCurrentPage(1);
  };

  const showNetworkErrorMessage = () => {
    return(
      <ErrorSimple
        message={
          'We have been unable to retrieve active appeals at the moment. Please try later or contact us via the contact page'
        }
        padding={'0.5rem'}
        margin={''}
        icon={'bi bi-exclamation-square-fill'}
        iconFontSize={'4rem'}
        textFontSize={''}
      />  
    );
  };

  const renderNoBlobsFoundMessage = () => {       
    return (
      <PhaseBanner
        tag={'No Appeals'}
        link={'/contact-us'}
        text1={'It appears there are no active Appeals.'}
        text2={'if you believe this to be incorrect.'}
        linkText={'Contact us'}
      />
    );    
  };
  
  const renderLoader = () => {
    if (isFetchingData)  
      return <Loader loadingText={'getting appeals'} padding={''} />;      
  };

   const renderAppeals = () => {
    return (
      <div className="d-flex flex-column" >
        <PagingSg
          numberOfPages={Math.ceil(filteredAppeals.length / itemsInAPage)}
          numberOfItems={filteredAppeals.length}
          currentPage={currentPage}
          numberOfPageLinks={5}
          pageClicked={childPageClicked}
        />
        <AppealsTable
          data={currentAppeals}
          columns={AppealsParentColumns}
          getRowCanExpand={() => true}
          renderSubComponent={renderSubComponent}
        />
      </div>
    );
  };

  const renderNoAppealsFoundMessage = () => {
    return (
      <PhaseBanner
        tag={'No Appeals Found'}
        link={'/appeals'}
        text1={
          'No appeals found based on the search term.'
        }
        text2={'Please change the search term, or select Reset filters link in order to remove all filters and the search term'}
        linkText={'Reset filters.'}
      />
    );         
  };

  const renderAppealsTableOrError = () => {    
    if (networkError) return showNetworkErrorMessage();
    else {                      
      if (noBlobs) return renderNoBlobsFoundMessage();
      if (appeals && currentAppeals.length > 0) return renderAppeals();                                   
      if (!noBlobs && currentAppeals.length === 0 && !isFetchingData) return renderNoAppealsFoundMessage();               
    }
  };

  return (
      <>
        <PageHeaderSg headerText={'Active Appeals'} />
  
        <p>
          The details of active appeals (appeals that have not yet been determined by the Tribunal) are listed below.
        </p>
        <p>
          When searching for active appeal information, you can use the drop down menus or type into the free text search box. Additional fields you can search for include: property address, assessor reference, property type, net annual value, rateable value, effective date and appeal reason.
        </p>
        <p>The list is a live list and is constantly changing.</p>

        <div className="d-flex flex-sm-column flex-md-row filter-row appeals-filter-row">
          <div className="filter">
            <SelectComponent 
              selectId={'appeal-type-filter'} 
              selectedValue={appealTypeFilter} 
              selectedDisabled={false} 
              selectLabelText={'Appeal Type'} 
              selectName={'Appeal Type Filter'}
              selectOptions={appealTypes}
              onChange={onAppealTypeFilterChange} 
            />
          </div>
          <div className="filter">
            <SelectComponent 
              selectId={'appeal-sub-type'} 
              selectedValue={appealSubTypeFilter} 
              selectedDisabled={appealTypeFilter !== "All" ? false : true} 
              selectLabelText={'Appeal Subtype'} 
              selectName={'Appeal Subtype Filter'}
              selectOptions={appealSubTypes}
              onChange={onAppealSubTypeFilterChange} 
            />
          </div>
          <div className="filter">
            <SelectComponent 
              selectId={'respondent-filter'} 
              selectedValue={respondentFilter} 
              selectedDisabled={false} 
              selectLabelText={'Respondent'} 
              selectName={'Respondent Filter'}
              selectOptions={respondents} 
              onChange={onRespondentFilterChange} 
            />
          </div>
        </div>
        <div className="d-flex flex-sm-column flex-md-row search-row">
          <div className="search">
            <SearchAsYouTypeComponent 
              inputId={"search"} 
              inputValue={searchTerm}
              labelText={"Search all fields, e.g. date/time, etc. (min 3 characters)"} 
              inputType={"text"} 
              placeholder={"Search as you type..."} 
              searchLabel={"search"} 
              onSearch={(e: string) => {
                onSearchBoxChange(e);
              }} 
            /> 
          </div>
          <div className='search-row-r-aligned-buttons flex-sm-column flex-md-row'>
            <div className='excel-export-button'>
              <ExcelExport data={exportableAppeals} excelExportParameters={appealsExcelExportParams} />
            </div>
            <div className="clear-button">
              <PrimaryButton
                buttonText={'Clear All'}
                onClick={clearSearch} 
              />
            </div>
          </div>
        </div>
        <div className="d-flex flex-sm-column flex-md-row search-row">
          <p className='nr-of-results'>Number of appeals: {filteredAppeals.length}</p>
        </div>                
        {renderLoader()}
        {renderAppealsTableOrError()}
      </>
    );
};

export default Appeals;
